import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  Link,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, CloudUpload } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import AWS from 'aws-sdk';
import { useAuthToken } from "../../TokenContext";
import { useCredentials } from '../../useCredentials';
import { useIntl } from "react-intl";

// Styled components
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e0e0e0',
  width: '100%',
  maxWidth: '100%',
}));

const CenteredContainer = styled(Container)(({ theme, collapsed }) => ({
  maxWidth: collapsed ? '1400px !important' : '1200px !important',
  width: `calc(100% - ${collapsed ? '100px' : '280px'})`,
  margin: '0 auto',
  padding: theme.spacing(2),
  marginLeft: collapsed ? '80px' : '260px',
  marginRight: '20px',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    maxWidth: collapsed ? '1200px !important' : '1000px !important',
    width: `calc(100% - ${collapsed ? '80px' : '260px'})`,
    marginLeft: collapsed ? '60px' : '240px',
    marginRight: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100% !important',
    width: 'calc(100% - 20px)',
    marginLeft: '10px',
    marginRight: '10px',
    padding: theme.spacing(1),
  }
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
  backgroundColor: theme.palette.mode === 'dark' ? '#2B2B2B' : '#f8f9fa'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#2196f3',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: '8px',
  background: 'linear-gradient(45deg, #e3f2fd 30%, #bbdefb 90%)',
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)',
}));

const ColoredButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
  borderRadius: '8px',
  border: 0,
  color: 'white',
  padding: '12px 24px',
  boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px 2px rgba(33, 150, 243, .4)',
  }
}));

function PartnerSettings({ collapsed }) {
  const { credentials, isLoaded, isLoading, refresh } = useCredentials();
  const intl = useIntl();

  // S3 Configuration - using slack-transcribe bucket
  const s3Config = useMemo(() => ({
    bucketName: 'slack-transcribe',
    region: credentials.region || 'us-east-1', // fallback region
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);

  const [showPassword, setShowPassword] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingGCPFile, setUploadingGCPFile] = useState(false);
  
  const token = useAuthToken();
  const { user } = useSelector((state) => state.auth);

  // State variables
  const [fetchlist, setFetchlist] = useState([]);
  const [fetchlistid, setFetchlistid] = useState('');
  const [ARTsList, setARTsList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedGCPFile, setSelectedGCPFile] = useState(null);
  const [selectedARTId, setSelectedARTId] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedGCPFileName, setUploadedGCPFileName] = useState('');
const [selectedAIProvider, setSelectedAIProvider] = useState('open_ai_token');
  const [formData, setFormData] = useState({
    Jira_URL: '',
    Jira_admin_login_email_address: '',
    Jira_admin_API_Token: '',
    Github_username: '',
    Github_API_Token: '',
    Github_Repo_name: '',
    ART_id: '',
    Organizational_Code: user?.data?.[0]?.partner_code || '',
    Jira_Kanben_Project_name: '',
    circleci_github_username: '',
    circleci_github_repo: '',
    open_ai_token: '',
     gemini_key: '',
    circleci_api_token: '',
    sonarqube_cloud_url: '',
    sonarqube_api_token: '',
    aws_access_key_id: '',
    aws_secret_access_key: '',
    bitbucket_username: '',
    bitbucket_app_password: '',
    bitbucket_workspace: '',
    aws_region: '',
    jira_open_story_bug_status: [],
    jira_closed_story_bug_status: [],
    jira_story_points_calc_file: '',
    Google_application_credentions: '',
    gcp_project_id: '',
    gcp_dataset_id: '',
    gcp_table_id: '',
    sonarqube_project_key: '',
    slack_api_token: '',
    jira_project_key: '',
    ado_organization_name: '',
  ado_admin_mail: '',
  ado_project_name: '',
  ado_personal_access_token: '',
  ado_team: '',
  ado_open_status: [],
  ado_closed_status: [],
  });

  const [openStatusOptions] = useState([
    'To Do',
    'In Progress',
    'Testing',
    'In Review',
    'Blocked',
    'Ready for QA',
    'Open',
    'Reopened',
    'Verify-Dev',
    'Redo-Dev',
    'Done-Dev',
    'Verify-Prod',
    'Redo-Prod'
  ]);

  const [closedStatusOptions] = useState([
    'Done',
    'Closed',
    'Completed',
    'Resolved',
    'Verified',
    'Done-PROD'
  ]);

  // Handlers
  const handleTogglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadingFile(true);
      try {
        const fileName = await uploadFileToS3(file, 'story-points');
        setUploadedFileName(fileName);
        setFormData(prev => ({
          ...prev,
          jira_story_points_calc_file: fileName
        }));
        handleSnackbarOpen('Story points file uploaded successfully', 'success');
      } catch (error) {
        handleSnackbarOpen('Failed to upload story points file', 'error');
        setSelectedFile(null);
      } finally {
        setUploadingFile(false);
      }
    }
  };

  const handleGCPFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate that it's a JSON file
      if (!file.name.toLowerCase().endsWith('.json')) {
        handleSnackbarOpen('Please select a valid JSON file for GCP credentials', 'error');
        return;
      }
      
      setSelectedGCPFile(file);
      setUploadingGCPFile(true);
      try {
        const fileName = await uploadFileToS3(file);
        setUploadedGCPFileName(fileName);
        setFormData(prev => ({
          ...prev,
          Google_application_credentions: fileName
        }));
        handleSnackbarOpen('GCP credentials file uploaded successfully', 'success');
      } catch (error) {
        handleSnackbarOpen('Failed to upload GCP credentials file', 'error');
        setSelectedGCPFile(null);
      } finally {
        setUploadingGCPFile(false);
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleARTSelect = (event) => {
    const selectedARTId = event.target.value;
    setSelectedARTId(selectedARTId);
    setFormData(prev => ({
      ...prev,
      ART_id: selectedARTId
    }));
  };

  const handleOpenStatusChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      jira_open_story_bug_status: value
    }));
  };

  const handleClosedStatusChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      jira_closed_story_bug_status: value
    }));
  };

  const handleSnackbarOpen = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  // Enhanced S3 upload function with specific bucket and folder structure
  const uploadFileToS3 = async (file, folder = '') => {
    if (!file) return null;

    try {
      // Configure AWS
      AWS.config.update({
        region: s3Config.region,
        accessKeyId: s3Config.accessKeyId,
        secretAccessKey: s3Config.secretAccessKey,
      });

      const s3 = new AWS.S3();
      
      // Generate unique filename with timestamp and original extension
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      const sanitizedBaseName = baseName.replace(/[^a-zA-Z0-9-_]/g, '_'); // Sanitize filename
      
      // Create folder structure: folder/timestamp-filename.extension
      const s3Key = folder ? `${folder}/${timestamp}-${sanitizedBaseName}.${fileExtension}` : `${timestamp}-${sanitizedBaseName}.${fileExtension}`;
      
      const params = {
        Bucket: s3Config.bucketName,
        Key: s3Key,
        Body: file,
        ContentType: file.type,
        ACL: 'private', // Changed to private for better security
        Metadata: {
          'original-filename': file.name,
          'upload-timestamp': timestamp.toString(),
          'uploaded-by': user?.data?.[0]?.partner_code || 'unknown'
        }
      };

      console.log('Uploading to S3 with params:', {
        bucket: params.Bucket,
        key: params.Key,
        contentType: params.ContentType
      });

      const uploadResult = await s3.upload(params).promise();
      
      console.log('S3 upload successful:', uploadResult);
      
      // Return just the filename (Key) to be stored in the database
      return uploadResult.Key;
      
    } catch (error) {
      console.error('Error uploading to S3:', error);
      
      // Provide more specific error messages
      if (error.code === 'CredentialsError') {
        throw new Error('AWS credentials are invalid or missing');
      } else if (error.code === 'NoSuchBucket') {
        throw new Error('S3 bucket does not exist or is not accessible');
      } else if (error.code === 'AccessDenied') {
        throw new Error('Access denied to S3 bucket');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  };

  // Form submission handlers
  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      // Prepare the data with uploaded filenames
      const formattedData = {
        ...formData,
        jira_story_points_calc_file: uploadedFileName || formData.jira_story_points_calc_file,
        Google_application_credentions: uploadedGCPFileName || formData.Google_application_credentions,
         open_ai_token: selectedAIProvider === 'open_ai_token' ? formData.open_ai_token : null,
  gemini_key: selectedAIProvider === 'gemini_key' ? formData.gemini_key : null,
        jira_open_story_bug_status: Array.isArray(formData.jira_open_story_bug_status)
          ? formData.jira_open_story_bug_status
          : [formData.jira_open_story_bug_status],
        jira_closed_story_bug_status: Array.isArray(formData.jira_closed_story_bug_status)
          ? formData.jira_closed_story_bug_status
          : [formData.jira_closed_story_bug_status],
           ado_open_status: Array.isArray(formData.ado_open_status)
    ? formData.ado_open_status
    : [formData.ado_open_status],
  ado_closed_status: Array.isArray(formData.ado_closed_status)
    ? formData.ado_closed_status
    : [formData.ado_closed_status],
      };

      console.log('Submitting form data:', formattedData);

      const response = await fetch(process.env.REACT_APP_CUSTOMERADMIN_AUTHORIZATIONTOKEN, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (response.ok) {
        handleSnackbarOpen('Settings saved successfully');
        // Optionally reload or update the UI
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const errorData = await response.text();
        console.error('API Error:', errorData);
        handleSnackbarOpen('Failed to save settings', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      handleSnackbarOpen('An error occurred while saving settings', 'error');
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    try {
      // Use uploaded filenames if available, otherwise keep existing ones
      const formattedData = {
        ...formData,
        jira_story_points_calc_file: uploadedFileName || formData.jira_story_points_calc_file,
        Google_application_credentions: uploadedGCPFileName || formData.Google_application_credentions,
        open_ai_token: selectedAIProvider === 'open_ai_token' ? formData.open_ai_token : null,
  gemini_key: selectedAIProvider === 'gemini_key' ? formData.gemini_key : null,
        jira_open_story_bug_status: Array.isArray(formData.jira_open_story_bug_status)
          ? formData.jira_open_story_bug_status
          : [formData.jira_open_story_bug_status],
        jira_closed_story_bug_status: Array.isArray(formData.jira_closed_story_bug_status)
          ? formData.jira_closed_story_bug_status
          : [formData.jira_closed_story_bug_status],
           ado_open_status: Array.isArray(formData.ado_open_status)
        ? formData.ado_open_status
        : [formData.ado_open_status],
      ado_closed_status: Array.isArray(formData.ado_closed_status)
        ? formData.ado_closed_status
        : [formData.ado_closed_status],
      };

      console.log('Updating form data:', formattedData);

      const response = await axios.post(
        process.env.REACT_APP_CUSTOMERADMIN_UPDATEAUTHORIZATIONTOKENS,
        {
          id: fetchlistid.id,
          ...formattedData,
        },
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200) {
        handleSnackbarOpen('Settings updated successfully');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        handleSnackbarOpen('Failed to update settings', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      handleSnackbarOpen('An error occurred while updating settings', 'error');
    }
  };

  // Data fetching functions
  const fetchARTsList = async () => {
    if (!token) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_CUSTOMERADMIN_GENERICGIGLIST,
        { Organizational_Code: user.data[0].partner_code },
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 200 && response.data?.data) {
        setARTsList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ARTs list:', error);
      handleSnackbarOpen('Failed to fetch ARTs list', 'error');
    }
  };

  const fetchauthorisationlist = async () => {
    if (!token) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_CUSTOMERADMIN_FETCHAUTHORIZATIONTOKENS,
        { Organizational_Code: user.data[0].partner_code },
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data?.body?.data?.length > 0) {
        const fetchData = response.data.body.data[0];
        setFetchlist(response.data.body.data);
        setFetchlistid(fetchData);
        if (fetchData.gemini_key && fetchData.gemini_key.trim() !== '') {
          setSelectedAIProvider('gemini_key');
        } else {
          setSelectedAIProvider('open_ai_token');
        }
        setFormData(prev => ({
          ...prev,
          ...fetchData,
          jira_open_story_bug_status: Array.isArray(fetchData.jira_open_story_bug_status) 
            ? fetchData.jira_open_story_bug_status 
            : fetchData.jira_open_story_bug_status?.[0]?.split(',') || [],
          jira_closed_story_bug_status: Array.isArray(fetchData.jira_closed_story_bug_status)
            ? fetchData.jira_closed_story_bug_status
            : fetchData.jira_closed_story_bug_status?.[0]?.split(',') || [],
             ado_open_status: Array.isArray(fetchData.ado_open_status)
    ? fetchData.ado_open_status
    : fetchData.ado_open_status?.[0]?.split(',') || [],
  ado_closed_status: Array.isArray(fetchData.ado_closed_status)
    ? fetchData.ado_closed_status
    : fetchData.ado_closed_status?.[0]?.split(',') || [],
        }));
      }
    } catch (error) {
      console.error('Error fetching authorization list:', error);
      handleSnackbarOpen('Failed to fetch current settings', 'error');
    }
  };

  // Effects
  useEffect(() => {
    fetchARTsList();
    fetchauthorisationlist();
  }, [token]);

  // Helper function to generate S3 URL for file access
  const getS3FileUrl = (fileName) => {
    if (!fileName) return null;
    return `https://${s3Config.bucketName}.s3.${s3Config.region}.amazonaws.com/${encodeURIComponent(fileName)}`;
  };

  // Render method
  return (
    <CenteredContainer collapsed={collapsed}>
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {intl.formatMessage({ id: "settings" })}
        </Typography>

        <StyledPaper elevation={3}>
          <form onSubmit={fetchlist.length === 0 ? handleSubmit : handleUpdate}>
            <Grid container spacing={3}>
              {/* ART Selection */}
              <Grid item xs={12}>
                <SectionContainer>
                  <StyledFormControl>
                    <InputLabel>{intl.formatMessage({ id: "selectART" })}</InputLabel>
                    <Select
                      value={formData.ART_id}
                      onChange={handleARTSelect}
                      label={intl.formatMessage({ id: "selectART" })}
                    >
                      <MenuItem value="">
                        <em>{intl.formatMessage({ id: "selectAnART" })}</em>
                      </MenuItem>
                      {ARTsList.map((art) => (
                        <MenuItem key={art.ART_id} value={art.ART_id}>
                          {art.ART_Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </SectionContainer>
              </Grid>

              {/* Jira Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">{intl.formatMessage({ id: "jiraConfiguration" })}</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jira Project Key"
                  name="Jira_Kanben_Project_name"
                  value={formData.Jira_Kanben_Project_name}
                  onChange={handleChange}
                  placeholder="PROJECT-KEY"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jira URL"
                  name="Jira_URL"
                  value={formData.Jira_URL}
                  onChange={handleChange}
                  placeholder="https://your-domain.atlassian.net"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jira Admin Email"
                  name="Jira_admin_login_email_address"
                  type="email"
                  value={formData.Jira_admin_login_email_address}
                  onChange={handleChange}
                  placeholder="admin@company.com"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jira Admin API Token"
                  name="Jira_admin_API_Token"
                  type={showPassword.Jira_admin_API_Token ? 'text' : 'password'}
                  value={formData.Jira_admin_API_Token}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('Jira_admin_API_Token')}
                          edge="end"
                        >
                          {showPassword.Jira_admin_API_Token ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Jira Status Configuration */}
              <Grid item xs={12} md={6}>
                <StyledFormControl>
                  <Autocomplete
                    multiple
                    options={openStatusOptions}
                    value={formData.jira_open_story_bug_status || []}
                    onChange={handleOpenStatusChange}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jira Open Story/Bug Status"
                        placeholder="Select or type status"
                      />
                    )}
                  />
                </StyledFormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledFormControl>
                  <Autocomplete
                    multiple
                    options={closedStatusOptions}
                    value={formData.jira_closed_story_bug_status || []}
                    onChange={handleClosedStatusChange}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jira Closed Story/Bug Status"
                        placeholder="Select or type status"
                      />
                    )}
                  />
                </StyledFormControl>
              </Grid>

              {/* GitHub Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">GitHub Configuration</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GitHub Username"
                  name="Github_username"
                  value={formData.Github_username}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GitHub API Token"
                  name="Github_API_Token"
                  type={showPassword.Github_API_Token ? 'text' : 'password'}
                  value={formData.Github_API_Token}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('Github_API_Token')}
                          edge="end"
                        >
                          {showPassword.Github_API_Token ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GitHub Repository Name"
                  name="Github_Repo_name"
                  value={formData.Github_Repo_name}
                  onChange={handleChange}
                />
              </Grid>

              {/* CircleCI Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">CircleCI Configuration</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CircleCI GitHub Username"
                  name="circleci_github_username"
                  value={formData.circleci_github_username}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CircleCI GitHub Repository"
                  name="circleci_github_repo"
                  value={formData.circleci_github_repo}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CircleCI API Token"
                  name="circleci_api_token"
                  type={showPassword.circleci_api_token ? 'text' : 'password'}
                  value={formData.circleci_api_token}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('circleci_api_token')}
                          edge="end"
                        >
                          {showPassword.circleci_api_token ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
{/* Azure DevOps Section */}
<Grid item xs={12}>
  <SectionTitle variant="h6">Azure DevOps Configuration</SectionTitle>
  <Divider />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label="ADO Organization Name"
    name="ado_organization_name"
    value={formData.ado_organization_name}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label="ADO Admin Email"
    name="ado_admin_mail"
    type="email"
    value={formData.ado_admin_mail}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label="ADO Project Name"
    name="ado_project_name"
    value={formData.ado_project_name}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label="ADO Personal Access Token"
    name="ado_personal_access_token"
    type={showPassword.ado_personal_access_token ? 'text' : 'password'}
    value={formData.ado_personal_access_token}
    onChange={handleChange}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            onClick={() => handleTogglePassword('ado_personal_access_token')}
            edge="end"
          >
            {showPassword.ado_personal_access_token ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label="ADO Team"
    name="ado_team"
    value={formData.ado_team}
    onChange={handleChange}
  />
</Grid>

<Grid item xs={12} md={6}>
  <StyledFormControl>
    <Autocomplete
      multiple
      options={openStatusOptions}
      value={formData.ado_open_status || []}
      onChange={(event, value) => setFormData(prev => ({ ...prev, ado_open_status: value }))}
      freeSolo
      renderInput={(params) => (
        <TextField
          {...params}
          label="ADO Open Status"
          placeholder="Select or type status"
        />
      )}
    />
  </StyledFormControl>
</Grid>

<Grid item xs={12} md={6}>
  <StyledFormControl>
    <Autocomplete
      multiple
      options={closedStatusOptions}
      value={formData.ado_closed_status || []}
      onChange={(event, value) => setFormData(prev => ({ ...prev, ado_closed_status: value }))}
      freeSolo
      renderInput={(params) => (
        <TextField
          {...params}
          label="ADO Closed Status"
          placeholder="Select or type status"
        />
      )}
    />
  </StyledFormControl>
</Grid>
              {/* SonarQube Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">SonarQube Configuration</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SonarQube Cloud URL"
                  name="sonarqube_cloud_url"
                  value={formData.sonarqube_cloud_url}
                  onChange={handleChange}
                  placeholder="https://sonarcloud.io"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SonarQube API Token"
                  name="sonarqube_api_token"
                  type={showPassword.sonarqube_api_token ? 'text' : 'password'}
                  value={formData.sonarqube_api_token}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('sonarqube_api_token')}
                          edge="end"
                        >
                          {showPassword.sonarqube_api_token ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* AWS Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">AWS Configuration</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="AWS Access Key ID"
                  name="aws_access_key_id"
                  value={formData.aws_access_key_id}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "awsSecretAccessKey" })}
                  name="aws_secret_access_key"
                  type={showPassword.aws_secret_access_key ? 'text' : 'password'}
                  value={formData.aws_secret_access_key}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('aws_secret_access_key')}
                          edge="end"
                        >
                          {showPassword.aws_secret_access_key ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "awsRegion" })}
                  name="aws_region"
                  value={formData.aws_region}
                  onChange={handleChange}
                />
              </Grid>

              {/* BitBucket Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">{intl.formatMessage({ id: "bitBucketConfiguration" })}</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "bitBucketUsername" })}
                  name="bitbucket_username"
                  value={formData.bitbucket_username}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "bitBucketAppPassword" })}
                  name="bitbucket_app_password"
                  type={showPassword.bitbucket_app_password ? 'text' : 'password'}
                  value={formData.bitbucket_app_password}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('bitbucket_app_password')}
                          edge="end"
                        >
                          {showPassword.bitbucket_app_password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "bitBucketWorkspace" })}
                  name="bitbucket_workspace"
                  value={formData.bitbucket_workspace}
                  onChange={handleChange}
                />
              </Grid>

              {/* OpenAI Section */}
              {/* <Grid item xs={12}>
                <SectionTitle variant="h6">{intl.formatMessage({ id: "openAIConfiguration" })}</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: "openAIToken" })}
                  name="open_ai_token"
                  type={showPassword.open_ai_token ? 'text' : 'password'}
                  value={formData.open_ai_token}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleTogglePassword('open_ai_token')}
                          edge="end"
                        >
                          {showPassword.open_ai_token ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid> */}

{/* AI Configuration Section */}
<Grid item xs={12}>
  <SectionTitle variant="h6">AI Configuration</SectionTitle>
  <Divider />
</Grid>

<Grid item xs={12} md={6}>
  <StyledFormControl>
    <InputLabel>AI Provider</InputLabel>
    <Select
      value={selectedAIProvider}
      onChange={(e) => setSelectedAIProvider(e.target.value)}
      label="AI Provider"
    >
      <MenuItem value="open_ai_token">OpenAI</MenuItem>
      <MenuItem value="gemini_key">Google Gemini</MenuItem>
    </Select>
  </StyledFormControl>
</Grid>

<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label={selectedAIProvider === 'open_ai_token' ? 'OpenAI Token' : 'Gemini API Key'}
    name={selectedAIProvider}
    type={showPassword[selectedAIProvider] ? 'text' : 'password'}
    value={formData[selectedAIProvider] || ''}
    onChange={handleChange}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            onClick={() => handleTogglePassword(selectedAIProvider)}
            edge="end"
          >
            {showPassword[selectedAIProvider] ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
</Grid>
              {/* Story Points Calculation Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">{intl.formatMessage({ id: "storyPointsCalculation" })}</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={uploadingFile ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                    disabled={uploadingFile}
                    sx={{ 
                      width: 'fit-content',
                      maxWidth: '350px',
                      px: 2,
                      py: 1,
                      fontSize: '0.875rem',
                      background: uploadingFile ? 'rgba(0,0,0,0.12)' : undefined
                    }}
                  >
                    {uploadingFile ? 'Uploading...' : intl.formatMessage({ id: "uploadStoryPointsCalculationFile" })}
                    <VisuallyHiddenInput 
                      type="file" 
                      onChange={handleFileChange}
                      disabled={uploadingFile}
                    />
                  </Button>
                  
                  {uploadingFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Uploading story points file to S3...
                      </Typography>
                    </Box>
                  )}

                  {selectedFile && uploadedFileName && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'success.light', borderRadius: 1, maxWidth: 500 }}>
                      <Typography variant="body2" color="success.dark">
                        ✓ Successfully uploaded: {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        wordBreak: 'break-word',
                        display: 'block',
                        mt: 0.5
                      }}>
                        S3 Key: {uploadedFileName}
                      </Typography>
                    </Box>
                  )}

                  {formData.jira_story_points_calc_file && !selectedFile && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'info.light', borderRadius: 1, maxWidth: 500 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Current story points file:
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ 
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                        fontSize: '0.8rem'
                      }}>
                        {formData.jira_story_points_calc_file}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Google Cloud Platform Section */}
              <Grid item xs={12}>
                <SectionTitle variant="h6">Google Cloud Platform Configuration</SectionTitle>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GCP Project ID"
                  name="gcp_project_id"
                  value={formData.gcp_project_id}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GCP Dataset ID"
                  name="gcp_dataset_id"
                  value={formData.gcp_dataset_id}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GCP Table ID"
                  name="gcp_table_id"
                  value={formData.gcp_table_id}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={uploadingGCPFile ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                    disabled={uploadingGCPFile}
                    sx={{ 
                      width: 'fit-content',
                      maxWidth: '350px',
                      px: 2,
                      py: 1,
                      fontSize: '0.875rem',
                      background: uploadingGCPFile ? 'rgba(0,0,0,0.12)' : undefined
                    }}
                  >
                    {uploadingGCPFile ? 'Uploading...' : 'Upload GCP Credentials'}
                    <VisuallyHiddenInput 
                      type="file" 
                      onChange={handleGCPFileChange} 
                      accept=".json"
                      disabled={uploadingGCPFile}
                    />
                  </Button>
                  
                  {uploadingGCPFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Uploading GCP credentials to S3...
                      </Typography>
                    </Box>
                  )}

                  {selectedGCPFile && uploadedGCPFileName && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'success.light', borderRadius: 1, maxWidth: 500 }}>
                      <Typography variant="body2" color="success.dark">
                        ✓ Successfully uploaded: {selectedGCPFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        wordBreak: 'break-word',
                        display: 'block',
                        mt: 0.5
                      }}>
                        S3 Key: {uploadedGCPFileName}
                      </Typography>
                    </Box>
                  )}
                  
                  {formData.Google_application_credentions && !selectedGCPFile && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'info.light', borderRadius: 1, maxWidth: 500 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Current GCP credentials file:
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ 
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                        fontSize: '0.8rem'
                      }}>
                        {formData.Google_application_credentions}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <ColoredButton
                    type="submit"
                    size="large"
                    sx={{ minWidth: 200 }}
                    disabled={uploadingFile || uploadingGCPFile}
                  >
                    {uploadingFile || uploadingGCPFile ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Processing...
                      </>
                    ) : (
                      fetchlist.length === 0 
                        ? intl.formatMessage({ id: "submit" })
                        : intl.formatMessage({ id: "update" })
                    )}
                  </ColoredButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </StyledPaper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </CenteredContainer>
  );
}

export default PartnerSettings;